import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../api/config";
import LocationSelect from "../components/LocationSelect";
import "./StockMatrixPanel.css";

const PRETTYFIT_SIZES = ["35", "36", "37", "38", "39", "40"];

const HANGTEN_SIZES = [
  "24", "25", "26", "27", "28", "29", "30", "31", "32", "34", "36", "38",
  "XS", "S", "M", "L", "XL", "XXL"
];

function formatQty(value) {
  const n = Number(value || 0);
  if (!n) return "-";
  return n.toLocaleString();
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString();
}

export default function StockMatrixPanel() {
  const [location, setLocation] = useState("ALL");
  const [brand, setBrand] = useState("PRETTYFIT");
  const [productCode, setProductCode] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const sizeColumns = useMemo(() => {
    return brand === "PRETTYFIT" ? PRETTYFIT_SIZES : HANGTEN_SIZES;
  }, [brand]);

  async function loadMatrix() {
    try {
      setLoading(true);
      setError("");

      const query = new URLSearchParams();
      query.set("locationCode", location || "ALL");
      query.set("brand", brand || "ALL");

      if (productCode.trim()) {
        query.set("productCode", productCode.trim());
      }

      const response = await fetch(`${API_BASE}/api/erp/stock-matrix?${query.toString()}`);
      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to load stock matrix");
      }

      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMatrix();
  }, [location, brand]);

  return (
    <div className="stockMatrixPanel">
      <div className="stockMatrixPanel__head">
        <div>
          <div className="stockMatrixPanel__tag">STOCK MATRIX VIEW</div>
          <h3>Inventory Matrix View</h3>
          <p>Excel format အတိုင်း code, color, size qty နဲ့ price ကို ကြည့်ရန်။</p>
        </div>

        <button type="button" onClick={loadMatrix} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div className="stockMatrixPanel__filters">
        <label>
          <span>Location</span>
          <LocationSelect
            value={location}
            onChange={setLocation}
            type="ALL"
            includeAll={true}
            allLabel="All Locations"
          />
        </label>

        <label>
          <span>Brand</span>
          <select value={brand} onChange={(e) => setBrand(e.target.value)}>
            <option value="PRETTYFIT">PRETTYFIT</option>
            <option value="HANGTEN">HANGTEN</option>
          </select>
        </label>

        <label>
          <span>Search Code</span>
          <input
            value={productCode}
            onChange={(e) => setProductCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") loadMatrix();
            }}
            placeholder="Example: 3438R"
          />
        </label>

        <button type="button" onClick={loadMatrix} disabled={loading}>
          Search
        </button>
      </div>

      {error && <div className="stockMatrixPanel__error">{error}</div>}

      <div className="stockMatrixPanel__meta">
        <b>{rows.length}</b> matrix rows / Brand: <b>{brand}</b>
      </div>

      <div className="stockMatrixTableWrap">
        <table className="stockMatrixTable">
          <thead>
            <tr>
              {location === "ALL" && <th>Location</th>}
              <th>code</th>
              <th>color</th>
              {sizeColumns.map((size) => (
                <th key={size}>{size}</th>
              ))}
              <th>Total</th>
              <th>price</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={sizeColumns.length + 4 + (location === "ALL" ? 1 : 0)}
                  className="stockMatrixTable__empty"
                >
                  No stock matrix data.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={`${row.locationCode}-${row.productCode}-${row.color}-${index}`}>
                  {location === "ALL" && <td>{row.locationCode}</td>}
                  <td className="stockMatrixTable__code">{row.productCode}</td>
                  <td>{row.color || "-"}</td>

                  {sizeColumns.map((size) => {
                    const qty = Number(row.sizes?.[size] || 0);

                    return (
                      <td
                        key={size}
                        className={
                          qty < 0
                            ? "stockMatrixTable__negative"
                            : qty > 0
                              ? "stockMatrixTable__positive"
                              : ""
                        }
                      >
                        {formatQty(qty)}
                      </td>
                    );
                  })}

                  <td className="stockMatrixTable__total">{formatQty(row.totalQty)}</td>
                  <td>{formatMoney(row.priceMmk)} Ks</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
